/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import {
  EuiButton,
  EuiCallOut,
  EuiCheckableCard,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiPageTemplate,
  EuiSpacer,
  EuiSplitPanel,
  EuiText,
} from "@inscopix/ideas-eui";
import { getEnvVar } from "ideas.env";
import { useState } from "react";
import { COGNITO_ATTRIBUTE_KEYS } from "types/constants";

interface ChooseRegionProps {
  setChoseRegion: (choseRegion: boolean) => void;
  syncPersonalAndHomeRegion: boolean;
  setSyncPersonalAndHomeRegion: (sync: boolean) => void;
}

export const CreatePersonalSpaceChooseRegion = ({
  setChoseRegion,
  syncPersonalAndHomeRegion,
  setSyncPersonalAndHomeRegion,
}: ChooseRegionProps) => {
  const currentRegionKey = getEnvVar("CURRENT_REGION").KEY;
  const currentRegionName = getEnvVar("CURRENT_REGION").longName;
  const externalRegions = getEnvVar("EXTERNAL_REGIONS");
  const auth = useAuthenticator((s) => [s.user]);

  const homeRegionKey =
    auth.user.attributes?.[COGNITO_ATTRIBUTE_KEYS.HOME_REGION];

  // get the home region's name if we have it, otherwise use the key
  const homeRegionName =
    externalRegions.find((region) => region.KEY === homeRegionKey)?.longName ??
    homeRegionKey?.toUpperCase();

  const isHomeRegionMatch = homeRegionKey === currentRegionKey;
  const [isChecked, setIsChecked] = useState(false);

  return (
    <>
      <EuiPageTemplate.Section>
        <EuiText>
          <h2>Where would you like your data stored?</h2>
          <p>
            IDEAS data can be hosted in different regions - your IDEAS account
            allows you to access any of these regions, but you have to choose
            one for your personal space&apos;s data to live in.
          </p>
          <p>
            <EuiCallOut
              title="You can only create one personal space, so choose carefully!"
              iconType="iInCircle"
            >
              Generally you&apos;ll want to pick the region that is closest
              geographically to where you will be accessing it most, unless your
              data has specific requirements about where it can be hosted.
            </EuiCallOut>
          </p>
          <p></p>
          <p>
            You are currently creating a space in the <b>{currentRegionName}</b>{" "}
            region. Click the button below to confirm your choice and create
            your personal space:
          </p>
          <EuiCheckableCard
            id="set-region-current"
            label={
              <strong>I want my data stored in {currentRegionName}</strong>
            }
            checkableType="checkbox"
            value={currentRegionKey}
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
          >
            {isChecked && !isHomeRegionMatch && (
              <>
                <EuiText size="s">
                  <p>
                    We noticed your default login region is currently set to{" "}
                    <strong>{homeRegionName}</strong>. Check the box below if
                    you would like us to change this to{" "}
                    <strong>{currentRegionName}</strong> to match your personal
                    space region.
                  </p>
                </EuiText>
                <EuiSpacer size="s" />
                <EuiCheckbox
                  id={"sync-region"}
                  label="Redirect me to this region when I log in"
                  checked={syncPersonalAndHomeRegion}
                  onChange={() =>
                    setSyncPersonalAndHomeRegion(!syncPersonalAndHomeRegion)
                  }
                />
              </>
            )}
          </EuiCheckableCard>
          <EuiSpacer size="m" />
          {externalRegions.length > 0 && (
            <p>
              Or, if you wish to create your personal space in a different
              region, use the links below to navigate there:
              <EuiSpacer size="m" />
              <EuiFlexGroup
                direction="column"
                gutterSize="l"
                css={css`
                  button.euiPanel {
                    display: flex;
                  }
                `}
              >
                {externalRegions.map((region) => (
                  <EuiSplitPanel.Outer
                    direction="row"
                    key={region.KEY}
                    hasBorder={true}
                    onClick={() =>
                      window.open(
                        `${region.URL}/create-personal-space`,
                        "_self",
                      )
                    }
                  >
                    <EuiSplitPanel.Inner grow={false} color="subdued">
                      <EuiIcon type="popout" />
                    </EuiSplitPanel.Inner>
                    <EuiSplitPanel.Inner>
                      <EuiText>
                        <p>
                          I want my data stored in{" "}
                          <strong>{region.longName}</strong>
                        </p>
                      </EuiText>
                    </EuiSplitPanel.Inner>
                  </EuiSplitPanel.Outer>
                ))}
              </EuiFlexGroup>
            </p>
          )}
        </EuiText>
        <EuiSpacer />
        <EuiFlexGroup justifyContent="flexEnd">
          <EuiFlexItem grow={false}>
            <EuiButton
              disabled={!isChecked}
              fill
              onClick={() => setChoseRegion(true)}
            >
              Continue
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPageTemplate.Section>
    </>
  );
};
